import type {
  PageContext,
  PageProductInfo,
  PageType,
} from '../types/page-context.js';
import type {
  ProactiveGreetingPageType,
  ProactiveGreetingPhrase,
  ProactiveGreetingProduct,
  ProactiveGreetingQuickReply,
  ProactiveGreetingResponse,
} from '../types/proactive-greeting-api.js';
import type { ContextPopupDelayByPageType } from '../types/widget-settings.js';
import { resolveContextPopupDelaySeconds } from './context-popup-delay.js';
import {
  isGenericPageTitle,
  titleFromPathname,
} from './page.js';

/** Max chars for `page.description` in `POST /greeting`. */
export const PROACTIVE_GREETING_DESCRIPTION_MAX = 2000;

/** Map widget page parser types to greeting API `page.type`. */
export function toProactiveGreetingPageType(
  pageType: PageType,
): ProactiveGreetingPageType {
  if (pageType === 'home') return 'home';
  if (pageType === 'product') return 'product';
  if (pageType === 'category') return 'category';
  return 'other';
}

/** Trim and truncate description for greeting payload (word-boundary when possible). */
export function truncateProactiveGreetingDescription(
  value: string | undefined | null,
  max = PROACTIVE_GREETING_DESCRIPTION_MAX,
): string | undefined {
  const trimmed = value?.replace(/\s+/g, ' ').trim();
  if (!trimmed) return undefined;
  if (trimmed.length <= max) return trimmed;

  const slice = trimmed.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  const cut =
    lastSpace > Math.floor(max * 0.6) ? slice.slice(0, lastSpace) : slice;
  return `${cut.trimEnd()}…`;
}

/** Map page product into greeting `page.product` (omit empty / raw-only). */
export function toProactiveGreetingProduct(
  product: PageProductInfo | undefined,
): ProactiveGreetingProduct | undefined {
  if (!product) return undefined;

  const mapped: ProactiveGreetingProduct = {};
  if (product.name?.trim()) mapped.name = product.name.trim();
  if (product.price?.trim()) mapped.price = product.price.trim();
  if (product.currency?.trim()) mapped.currency = product.currency.trim();
  if (product.sku?.trim()) mapped.sku = product.sku.trim();
  if (product.availability) mapped.availability = product.availability;
  if (product.availabilityConfidence) {
    mapped.availability_confidence = product.availabilityConfidence;
  }

  return Object.keys(mapped).length > 0 ? mapped : undefined;
}

/**
 * Title for catalog search + AI context.
 * Prefers product name → page title → humanized pathname; skips generic home labels
 * so breadcrumb "Головна" / "Home" never become the search query.
 */
export function resolveProactiveGreetingPageTitle(
  context: Pick<PageContext, 'title' | 'product' | 'pathname'>,
): string | undefined {
  const candidates = [
    context.product?.name,
    context.title,
    titleFromPathname(context.pathname),
  ];

  for (const raw of candidates) {
    const trimmed = raw?.trim();
    if (trimmed && !isGenericPageTitle(trimmed)) return trimmed;
  }

  return undefined;
}

/** Non-empty text phrases from greeting API. */
export function filterProactiveGreetingPhrases(
  greeting: ProactiveGreetingPhrase[] | undefined | null,
): ProactiveGreetingPhrase[] {
  return (greeting ?? []).filter(
    (item) => item.type === 'text' && Boolean(item.payload?.trim()),
  );
}

/** Join assistant-style greeting phrases for popup bubble. */
export function formatProactiveGreetingText(
  greeting: ProactiveGreetingPhrase[],
): string {
  return filterProactiveGreetingPhrases(greeting)
    .map((item) => item.payload.trim())
    .join('\n\n');
}

/** Keep only complete quick replies with short label + full send text. */
export function filterProactiveGreetingQuickReplies(
  quickReplies: ProactiveGreetingQuickReply[] | undefined | null,
): ProactiveGreetingQuickReply[] {
  return (quickReplies ?? [])
    .map((item) => ({
      id: item.id?.trim() || undefined,
      label: item.label?.trim() || '',
      message: item.message?.trim() || '',
    }))
    .filter((item) => item.label && item.message);
}

/** Local test fallback until backend starts returning contextual quick replies. */
export function buildFallbackProactiveGreetingQuickReplies(
  locale: string | undefined,
  maxCount: 1 | 2 | 3 = 3,
): ProactiveGreetingQuickReply[] {
  const normalized = (locale ?? 'en').slice(0, 2).toLowerCase();
  const all: ProactiveGreetingQuickReply[] =
    normalized === 'uk' || normalized === 'ru'
      ? [
          {
            id: 'help',
            label: 'Підказати',
            message: 'Так, підкажіть мені, будь ласка.',
          },
          {
            id: 'question',
            label: 'Є питання',
            message: 'У мене є питання, допоможіть, будь ласка.',
          },
          {
            id: 'pick',
            label: 'Підібрати',
            message: 'Допоможіть мені підібрати варіант, будь ласка.',
          },
        ]
      : [
          {
            id: 'help',
            label: 'Help',
            message: 'Yes, please help me with this.',
          },
          {
            id: 'question',
            label: 'Question',
            message: 'I have a question, please help me.',
          },
          {
            id: 'pick',
            label: 'Choose',
            message: 'Please help me choose the right option.',
          },
        ];

  return all.slice(0, maxCount);
}

export function resolveProactiveGreetingQuickReplies(options: {
  response?: Pick<ProactiveGreetingResponse, 'quick_replies'> | null;
  locale?: string;
  /** Max chips to show (cabinet setting). Default 3. */
  maxCount?: 1 | 2 | 3;
}): ProactiveGreetingQuickReply[] {
  const maxCount = options.maxCount ?? 3;
  const fromResponse = filterProactiveGreetingQuickReplies(
    options.response?.quick_replies,
  );
  if (fromResponse.length > 0) return fromResponse.slice(0, maxCount);
  return buildFallbackProactiveGreetingQuickReplies(options.locale, maxCount);
}

/** Popup delay: backend `delay_seconds` when set, else settings fallback. */
export function resolveProactiveGreetingDelaySeconds(
  response: Pick<ProactiveGreetingResponse, 'delay_seconds'>,
  pageType: PageType,
  delayByPageType: ContextPopupDelayByPageType | undefined,
  fallbackSeconds: number,
): number {
  if (
    typeof response.delay_seconds === 'number' &&
    Number.isFinite(response.delay_seconds) &&
    response.delay_seconds >= 0
  ) {
    return response.delay_seconds;
  }
  return resolveContextPopupDelaySeconds(
    pageType,
    delayByPageType ?? {},
    fallbackSeconds,
  );
}
